import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { bookingTotal } from "./bookingFields";
import { PillTabs, StatusPill, EmptyState, PageSubtitle } from "../components/lux";
import { CalendarBlank } from "@phosphor-icons/react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "submitted", label: "Submitted" },
  { key: "pre_booking", label: "Pre-bookings" },
  { key: "current", label: "Current" },
  { key: "completed", label: "Completed" },
];

export default function MyBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const changeFilter = (val) => {
    setFilter(val);
    const next = new URLSearchParams(searchParams);
    next.set("filter", val);
    setSearchParams(next, { replace: true });
  };
  const openBooking = (row, list) => {
    const returnTo = `${location.pathname}?${searchParams.toString()}`;
    const listIds = (list || []).map((r) => r.id);
    navigate(`/bookings/view/${row.id}`, { state: { returnTo, listIds } });
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/bookings", { params: { mine: true } }); setRows(data); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const shown = useMemo(() => {
    const list = filter === "all" ? rows : rows.filter((b) => b.status === filter);
    return [...list].sort((a, b) => Date.parse(b.start_datetime || b.booking_date || 0) - Date.parse(a.start_datetime || a.booking_date || 0));
  }, [rows, filter]);

  const fmt = (s) => s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  const moneyVisible = rows.some((b) => b.total !== undefined || b.amount !== undefined);

  const tabs = FILTERS.map((f) => ({
    ...f,
    count: f.key === "all" ? rows.length : rows.filter((b) => b.status === f.key).length,
    testid: `my-tab-${f.key}`,
  }));

  return (
    <div className="space-y-6" data-testid="my-bookings-page">
      <PageSubtitle>
        {user?.salesperson || user?.name} · {rows.length} booking{rows.length === 1 ? "" : "s"} where you are the salesperson
      </PageSubtitle>

      <div data-testid="my-bookings-tabs">
        <PillTabs tabs={tabs} value={filter} onChange={changeFilter} testid="my-tab-pill" />
      </div>

      {loading ? <div className="p-12 text-center text-muted-foreground border border-border bg-card">Loading…</div>
        : shown.length === 0 ? (
          <div className="border border-border bg-card">
            <EmptyState icon={CalendarBlank} title="No bookings here yet" hint="Submitted bookings appear here as soon as you create them." />
          </div>
        ) : (
          <div className="border border-border bg-card overflow-x-auto" data-testid="my-bookings-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3.5 label-mono font-normal">Client</th>
                  <th className="text-left px-4 py-3.5 label-mono font-normal">Vehicle</th>
                  <th className="text-left px-4 py-3.5 label-mono font-normal">Period</th>
                  {moneyVisible && <th className="text-right px-4 py-3.5 label-mono font-normal">Total</th>}
                  <th className="text-right px-4 py-3.5 label-mono font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((b) => (
                  <tr key={b.id} onClick={() => openBooking(b, shown)} className="border-b border-border/50 last:border-b-0 hover:bg-white/[0.03] transition-colors cursor-pointer" data-testid={`my-booking-row-${b.id}`}>
                    <td className="px-4 py-4"><div className="font-heading text-base">{b.customer_name || "Guest"}</div><div className="label-mono mt-0.5">{b.booking_type}</div></td>
                    <td className="px-4 py-4 text-muted-foreground">{b.vehicle_label || "—"}</td>
                    <td className="px-4 py-4 text-xs tnum text-muted-foreground whitespace-nowrap">{fmt(b.start_datetime)}<br />→ {fmt(b.end_datetime)}</td>
                    {moneyVisible && (
                      <td className="px-4 py-4 text-right">
                        <span className="font-heading text-base money">${Number(bookingTotal(b)).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                        {b.tax_applied && <div className="text-[10px] text-muted-foreground">incl. HST</div>}
                      </td>
                    )}
                    <td className="px-4 py-4 text-right"><StatusPill status={b.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
